import React, { useState, useEffect, useRef } from 'react';
import '../assets/css/style.css';
import { useNavigate } from 'react-router-dom';
import Loader from './Loader';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Footer from './partials/footer';
import Turnstile from "react-turnstile";

const SignUp = () => {
    const [formData, setFormData] = useState({
        username: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        emailAddress: '',
        phone: '',
        facility: '',
        services: [],
        terms: true,
        state: '',
        stateName: '', // Added state name
        county: '',
        countyName: '', // Added county name
    });

    const [loading, setLoading] = useState(false);
    const [apiSubscriptionData, setApiSubscriptionData] = useState([]);
    const navigate = useNavigate();
    const [questions, setQuestions] = useState([]);
    const [statesWithCounties, setStatesWithCounties] = useState([]);
    const [counties, setCounties] = useState([]);
    const apiCalledRef = useRef();
    const [captchaToken, setCaptchaToken] = useState(null);

    useEffect(() => {
        if (!apiCalledRef.current) {
            apiCalledRef.current = true;
            fetchQuestions();
            fetchStatesWithCounties();
        }
        localStorage.clear();
    }, []);

    const fetchQuestions = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/facility_questions/`);
            if (!response.ok) throw new Error("Failed to fetch questions.");
            const data = await response.json();
            setQuestions(data.map(q => ({ ...q, selected: false })));
        } catch (err) {
            toast.error("Something went wrong, Please try again later");
            navigate('/error');
        }
    };

    const fetchStatesWithCounties = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/get-sponsor-state-county/`);
            if (!response.ok) throw new Error("Failed to fetch states/counties");
            const data = await response.json();
            setStatesWithCounties(data);
        } catch (err) {
            toast.error("Error loading states and counties.");
        }
    };

    const handleChange = (e, serviceId) => {
        const { type, checked, id, value } = e.target;

        if (type === 'checkbox' && serviceId) {
            setFormData((prevData) => ({
                ...prevData,
                services: checked
                    ? [...prevData.services, serviceId]
                    : prevData.services.filter((id) => id !== serviceId),
            }));
        } else if (type === 'checkbox') {
            setFormData((prevData) => ({
                ...prevData,
                [id]: checked,
            }));
        } else {
            if (id === 'state') {
                const selected = statesWithCounties.find((s) => s.id === parseInt(value));
                setCounties(selected ? selected.counties : []);
                setFormData((prevData) => ({
                    ...prevData,
                    state: value,
                    stateName: selected ? selected.name : '', // Store state name
                    county: '', // reset county
                    countyName: '', // reset county name
                }));
            } else if (id === 'county') {
                const selectedCounty = counties.find((c) => c.id === parseInt(value));
                setFormData((prevData) => ({
                    ...prevData,
                    county: value,
                    countyName: selectedCounty ? selectedCounty.name : '',
                }));
            } else {
                setFormData((prevData) => ({
                    ...prevData,
                    [id]: value,
                }));
            }
        }
    };

    const toggleQuestion = (questionId) => {
        setFormData((prevData) => {
            const isSelected = prevData.services.includes(questionId);
            return {
                ...prevData,
                services: isSelected
                    ? prevData.services.filter((id) => id !== questionId)
                    : [...prevData.services, questionId],
            };
        });
    };


    const fetchSubscriptions = async (subToken) => {
        setLoading(true)
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/subscriptions/all/`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Token ${subToken}`, // agar auth required ho
                    },
                }
            );

            if (!response.ok) {
                setLoading(false);
                throw new Error('Failed to fetch subscriptions');
            }

            const data = await response.json();
            setApiSubscriptionData(data)
            if (data?.checkout_url && data?.subscriptions?.length === 0) {
                window.location.href = data?.checkout_url
            } else {
                navigate('/dashboard')
            }
        } catch (error) {
            setLoading(false);
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (formData.password !== formData.confirmPassword) {
            toast.error("Passwords do not match");
            return;
        }
        setLoading(true);
        if (!captchaToken) {
            toast.error("Please verify captcha");
            setLoading(false);
            return;
        }
        const signupData = {
            turnstile_token: captchaToken,
            user: {
                username: formData.username,
                password: formData.password,
                conf_password: formData.confirmPassword,
                first_name: formData.firstName,
                last_name: formData.lastName,
                email: formData.emailAddress,
            },
            facility: {
                questions: formData.services,
                name: formData.facility,
                fcc_signup: false,
                contact_phone: formData.phone,
                state_sponsor: formData.state,
                state_sponsor_name: formData.stateName, // Added state name
                sponsor_county: formData.county,
                sponsor_county_name: formData.countyName, // Added county name
            },
            terms: {
                agree: formData.terms
            },
        };

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/signup/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(signupData),
            });

            const result = await response.json();

            if (!response.ok) {
                const errorData = result;
                if (Array.isArray(errorData.facility?.contact_phone) && errorData.facility.contact_phone[0] === "Number is not a valid US number!") {
                    toast.error('Number is not a valid US number!');
                } else if (Array.isArray(errorData.user?.username) && errorData.user.username[0] === "A user with that username already exists.") {
                    toast.error("A user with that username already exists.");
                } else {
                    toast.error(`Signup failed: ${errorData.message || "Please try again later."}`);
                }
                setLoading(false);
                return;
            }

            const loginResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/login/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    username: formData.username,
                    password: formData.password,
                    client_id: process.env.REACT_APP_CLIENT_ID,
                }),
            });

            const loginResult = await loginResponse.json();
            if (loginResponse.ok && loginResult.token) {
                localStorage.setItem("token", loginResult.token);
                fetchSubscriptions(loginResult.token)
            } else {
                alert(`Login failed: ${loginResult.message}`);
            }

        } catch (error) {
            toast.error('An error occurred during signup. Please try again later.');
        }
        setLoading(false);
    };

    return (
        <div className="App">
            {loading && (
                <div className="overlay">
                    <div className="loader-wrapper">
                        <Loader height={120} width={120} />
                    </div>
                </div>
            )}

            <ToastContainer />
            {/* <section id="content" className={`content-container ${loading ? 'blur' : ''}`}>
                <div className="page-signup">
                    <div className="signup-body">
                        <div className="row-div">
                            <div className="col signup-logo flex items-center justify-center">
                                <img src="/images/alf-logo.png" height="236" width="200" alt="Logo" />
                            </div>

                            <div className='message-box'>
                                <div className="notice-logo">
                                    <i className="fa-solid fa-triangle-exclamation"></i>
                                </div>
                                <div className="notice-text">
                                    <p className='textmessage'>
                                        This page is for <strong>Facility Admin Signup</strong> only.
                                        If you are an employee, please do not sign up here. You will receive an invitation email once your admin adds you to the employee list. If you haven't received it yet, please reach out to your facility administrator.
                                    </p>
                                </div>
                            </div>

                            <div className="col-md-12 signup-form">
                                <div className="form-div">
                                    <form className="form-horizontal" onSubmit={handleSubmit}>
                                        {[
                                            { label: 'Create Username', id: 'username', type: 'text' },
                                            { label: 'Create Password', id: 'password', type: 'password' },
                                            { label: 'Confirm Password', id: 'confirmPassword', type: 'password' },
                                            { label: 'First Name', id: 'firstName', type: 'text' },
                                            { label: 'Last Name', id: 'lastName', type: 'text' },
                                            { label: 'Email Address', id: 'emailAddress', type: 'email' },
                                            { label: 'Phone Number', id: 'phone', type: 'text' },
                                            { label: 'Facility Name', id: 'facility', type: 'text' },
                                        ].map((field, index) => (
                                            <div className="form-section" key={index}>
                                                <div className="col-sm-2 signup-page">
                                                    <label>{field.label}</label>
                                                </div>
                                                <div className="col-sm-10">
                                                    <input
                                                        type={field.type}
                                                        className="form-control input-lg"
                                                        required
                                                        id={field.id}
                                                        value={formData[field.id]}
                                                        onChange={handleChange}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                        <div className="form-section">
                                            <div className="col-sm-2 signup-page">
                                                <label>State</label>
                                            </div>
                                            <div className="col-sm-10">
                                                <select
                                                    className="form-control fix-select"
                                                    id="state"
                                                    value={formData.state}
                                                    onChange={handleChange}
                                                    required
                                                >
                                                    <option value="">Select State</option>
                                                    {statesWithCounties.map((state) => (
                                                        <option key={state.id} value={state.id}>{state.name}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="form-section">
                                            <div className="col-sm-2 signup-page">
                                                <label>County</label>
                                            </div>
                                            <div className="col-sm-10">
                                                <select
                                                    className="form-control fix-select"
                                                    id="county"
                                                    value={formData.county}
                                                    onChange={handleChange}
                                                    required
                                                >
                                                    <option value="">Select County</option>
                                                    {counties.map((county) => (
                                                        <option key={county.id} value={county.id}>{county.name}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        <div className="col-md-6 col-xs-12">
                                            <section className="panel panel-default">
                                                <div className="panel-headings">SERVICES</div>
                                                <div className="panel-body">
                                                    {["license", "non-license"].map((type) => (
                                                        <section key={type} className="panel panel-default" style={{ border: '1px solid #c2bfbf', borderRadius: '5px' }}>
                                                            <div className="panel-body">
                                                                <table className="table">
                                                                    <tbody>
                                                                        {questions
                                                                            .filter(q => (type === "license" ? q.is_license : !q.is_license))
                                                                            .map(question => (
                                                                                <tr key={question.id} className='service-row'>
                                                                                    <td width="100">
                                                                                        <label className="switch">
                                                                                            <input
                                                                                                type="checkbox"
                                                                                                checked={formData.services.includes(question.id)}
                                                                                                onChange={() => toggleQuestion(question.id)}
                                                                                            />
                                                                                            <i></i>
                                                                                        </label>
                                                                                    </td>
                                                                                    <td><span>{question.question}</span></td>
                                                                                </tr>
                                                                            ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </section>
                                                    ))}
                                                </div>
                                            </section>

                                            <div>
                                                <p>
                                                    <input
                                                        type="checkbox"
                                                        className='check-signup'
                                                        id="terms"
                                                        checked={formData.terms}
                                                        onChange={handleChange}
                                                        required
                                                    />
                                                    I agree to the
                                                    <a href="#/terms-and-conditions" target="_blank"> Terms and Conditions and Privacy Policy</a>.
                                                </p>
                                            </div>

                                            <button className="btn btn-info btn-block btn-lg" type="submit">
                                                Create free facility account
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                    <Footer />
                </div>
            </section> */}
            <div className="bg-gradient-to-br from-emerald-50 via-teal-50 to-blue-50  p-4">
                <div className=" lg:flex flex-col space-y-8 p-8 pt-0  liscensemobile-aling">
                    <div className="flex flex-col items-center justify-center space-y-8">
                        <div className="text-center space-y-6">
                            <div className="flex justify-center">
                                <img src="/images/alf-logo.png" alt="Logo" className="w-[30%]" />
                            </div>
                            <div className="space-y-2">
                                <p className="text-xl text-gray-600">
                                    Advanced Facility Management Platform
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-rose-200 border border-rose-400 text-center p-6 rounded-md max-w-4xl mx-auto mt-6">
                        <div className="flex flex-col items-center">
                            <svg className="w-12 h-12 text-rose-400 mb-4" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                    fillRule="evenodd"
                                    d="M8.257 3.099c.765-1.36 2.72-1.36 3.485 0l6.518 11.591c.75 1.335-.213 2.99-1.742 2.99H3.48c-1.53 0-2.492-1.655-1.742-2.99L8.257 3.1zM11 13a1 1 0 10-2 0 1 1 0 002 0zm-1-2a1 1 0 01-1-1V7a1 1 0 112 0v3a1 1 0 01-1 1z"
                                    clipRule="evenodd"
                                />
                            </svg>
                            <p className="text-rose-800 text-lg">
                                This page is for <span className="font-bold">Facility Admin Signup</span> only. If you are an employee, please do
                                not sign up here. You will receive an invitation email once your admin adds you to the employee list. If you
                                haven't received it yet, please reach out to your facility administrator.
                            </p>
                        </div>
                    </div>
                </div>
                <div className='flex items-center justify-center'>
                    <div className="w-full max-w-8xl grid lg:grid-cols-2 gap-8">
                        <div className="w-full flex justify-end">
                            <div className="w-[100%] lg:w-[70%] bg-white/90 backdrop-blur-sm rounded-2xl shadow-2xl border-0 p-8">
                                <div className="flex justify-center lg:hidden mb-6">
                                    <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center shadow-lg">
                                        <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                </div>

                                <div className="text-center lg:text-left ">
                                    <h1 className="font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mb-2 m-0">
                                        Create Your Account
                                    </h1>
                                    <p className="text-gray-600 !text-2xl text-base">Set up your facility account with ALFBOSS</p>
                                </div>
                                <form onSubmit={handleSubmit}>
                                    <div className='grid grid-cols-12 gap-4 mt-12'>
                                        {[
                                            { label: 'Create Username', id: 'username', type: 'text', icon: "fa-regular fa-user absolute left-3 !top-2/3 text-2xl -translate-y-1/2" },
                                            { label: 'Create Password', id: 'password', type: 'password', icon: "fa-solid fa-lock absolute left-3 !top-2/3 text-2xl -translate-y-1/2" },
                                            { label: 'Confirm Password', id: 'confirmPassword', type: 'password', icon: "fa-solid fa-lock absolute left-3 !top-2/3 text-2xl -translate-y-1/2" },
                                            { label: 'First Name', id: 'firstName', type: 'text' },
                                            { label: 'Last Name', id: 'lastName', type: 'text' },
                                            { label: 'Email Address', id: 'emailAddress', type: 'email', icon: "fa-regular fa-envelope absolute left-3 !top-2/3 text-2xl -translate-y-1/2" },
                                            { label: 'Phone Number', id: 'phone', type: 'text', icon: "fa-solid fa-phone-flip absolute left-3 !top-2/3 text-2xl -translate-y-1/2" },
                                            { label: 'Facility Name', id: 'facility', type: 'text', icon: "fa-regular fa-building absolute left-3 !top-2/3 text-2xl -translate-y-1/2" },
                                        ].map((field, index) => (
                                            <div className="relative col-span-12 md:col-span-12 lg:col-span-6" key={index}>
                                                <label className='text-2xl font-medium text-slate-700 block mb-2'>{field.label}</label>
                                                <input
                                                    type={field.type}
                                                    // className="form-control input-lg"
                                                    className='w-full pl-10 pr-2 py-3 text-2xl border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all duration-200 bg-white hover:border-gray-300 mb-2'

                                                    required
                                                    id={field.id}
                                                    value={formData[field.id]}
                                                    onChange={handleChange}
                                                />
                                                <i className={field?.icon}></i>
                                            </div>
                                        ))}
                                        <div className="relative col-span-12 md:col-span-12 lg:col-span-6">
                                            <label className='text-2xl font-medium text-slate-700 block mb-2'>State</label>
                                            <select
                                                className="w-full pl-4 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all duration-200 bg-white hover:border-gray-300"
                                                id="state"
                                                value={formData.state}
                                                onChange={handleChange}
                                                required
                                            >
                                                <option value="">Select State</option>
                                                {statesWithCounties.map((state) => (
                                                    <option key={state.id} value={state.id}>{state.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className=" relative col-span-12 md:col-span-12 lg:col-span-6">
                                            <label className='text-2xl font-medium text-slate-700 block mb-2'>County</label>
                                            <select
                                                className="w-full pl-4 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all duration-200 bg-white hover:border-gray-300"
                                                id="county"
                                                value={formData.county}
                                                onChange={handleChange}
                                                required
                                            >
                                                <option value="">Select County</option>
                                                {counties.map((county) => (
                                                    <option key={county.id} value={county.id}>{county.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    {/* </div> */}
                                    <div className="flex items-start space-x-3 p-4 rounded-lg border border-gray-200 bg-gray-50/80 mt-4 termandcond-mobileblock">
                                        <label className="inline-flex items-center space-x-2">
                                            <input
                                                type="checkbox"
                                                id="terms"
                                                checked={formData.terms}
                                                onChange={handleChange}
                                                className=" text-indigo-600 border-gray-300 rounded mr-1"
                                            />
                                            <div className="text-2xl text-gray-700 cursor-pointer leading-relaxed">I agree to the<span onClick={() => navigate('/terms-and-conditions')} className="text-emerald-600 hover:underline font-medium px-1">Terms and Conditions &  Privacy Policy</span>
                                            </div>
                                        </label>
                                    </div>
                                    
                                    <div className="space-y-4 liscensemobile-aling">
                                        <div className="flex items-center space-x-2 mb-4">
                                            <svg className="w-5 h-5 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                                            </svg>
                                            <h3 className="text-lg font-semibold text-gray-900 m-0">Services</h3>
                                        </div>

                                        {["license", "non-license"].map((type) => (
                                            <section key={type} className="space-y-3" >
                                                {questions
                                                    .filter(q => (type === "license" ? q.is_license : !q.is_license))
                                                    .map(question => (
                                                        <div key={question.id} className='flex items-center justify-between p-4 rounded-lg border border-gray-200 bg-white/60 backdrop-blur-sm hover:bg-white/80 transition-all duration-200'>
                                                            <div className='flex items-center space-x-3 w-[80%]'>

                                                                <span></span>
                                                                <div className='text-lg font-medium text-gray-700 cursor-pointer'>{question.question}</div>
                                                            </div>
                                                            <div
                                                                onClick={() => toggleQuestion(question.id)}
                                                                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 ${formData.services.includes(question.id) ? 'bg-emerald-600' : 'bg-gray-200'
                                                                    }`}
                                                            >
                                                                <span
                                                                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${formData.services.includes(question.id) ? 'translate-x-5' : 'translate-x-0'
                                                                        }`}
                                                                />
                                                            </div>
                                                        </div>
                                                    ))}
                                            </section>
                                        ))}
                                    </div>
                                    <div className="flex items-start space-x-3 p-4 rounded-lg border border-gray-200 bg-gray-50/80 mt-4 temandconditon-mobilealign">
                                        <label className="inline-flex items-center space-x-2">
                                            <input
                                                type="checkbox"
                                                id="terms"
                                                checked={formData.terms}
                                                onChange={handleChange}
                                                className=" text-indigo-600 border-gray-300 rounded mr-1"
                                            />
                                            <div className="text-xl text-gray-700 cursor-pointer leading-relaxed">I agree to the<span onClick={() => navigate('/terms-and-conditions')} className="text-emerald-600 hover:underline font-medium px-1">Terms and Conditions &  Privacy Policy</span>
                                            </div>
                                        </label>
                                    </div>
                                    <div className="mt-6 flex justify-center">
                                      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 shadow-sm">
                                        <Turnstile
                                          sitekey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
                                          theme="light"
                                          size="normal"
                                          onVerify={(token) => setCaptchaToken(token)}
                                          onExpire={() => setCaptchaToken(null)}
                                        />
                                      </div>
                                    </div>
                                    <button
                                        disabled={!captchaToken}
                                        className={"w-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-3 px-6 rounded-xl font-semibold shadow-lg shadow-emerald-200 hover:shadow-xl hover:shadow-emerald-300 transform hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center gap-2 group text-2xl mt-4"}
                                        type="submit">
                                        Create free facility account
                                    </button>

                                </form>
                                <div className="text-center pt-6">
                                    <p className="text-2xl text-gray-600">
                                        Already have an account?{" "}
                                        <a href="/signin" className="text-emerald-600 font-medium hover:underline hover:text-emerald-600">
                                            Sign in here
                                        </a>
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className=" w-[100%] lg:w-[80%] lg:flex flex-col space-y-8 p-8 pt-0 liscensehide-mobilealign">
                            <div className="flex flex-col items-center justify-center space-y-8">
                                <div className="text-center space-y-6">
                                    <div className="flex justify-center">
                                        <img src="/images/alf-logo.png" alt="Logo" className="w-[20%]" />
                                    </div>
                                    <div className="space-y-2">
                                        <p className="text-xl text-gray-600">
                                            Advanced Facility Management Platform
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-rose-200 border border-rose-400 text-center p-6 rounded-md max-w-4xl mx-auto mt-3 shadow animate-fade-in">
                                <div className="flex flex-col items-center">
                                    <svg className="w-12 h-12 text-rose-400 mb-4 animate-bounce" fill="currentColor" viewBox="0 0 20 20">
                                        <path
                                            fillRule="evenodd"
                                            d="M8.257 3.099c.765-1.36 2.72-1.36 3.485 0l6.518 11.591c.75 1.335-.213 2.99-1.742 2.99H3.48c-1.53 0-2.492-1.655-1.742-2.99L8.257 3.1zM11 13a1 1 0 10-2 0 1 1 0 002 0zm-1-2a1 1 0 01-1-1V7a1 1 0 112 0v3a1 1 0 01-1 1z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                    <p className="text-rose-800 text-3xl animate-fade-in">
                                        This page is for <span className="font-bold">Facility Admin Signup</span> only.
                                        If you are an employee, please do not sign up here. You will receive an invitation email once your admin adds you to the employee list.
                                        If you haven't received it yet, please reach out to your facility administrator.
                                    </p>
                                </div>
                            </div>

                            <div className="space-y-4 ">
                                <div className="flex items-center space-x-2 mb-4">
                                    <svg className="w-5 h-5 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                                    </svg>
                                    <h3 className="text-3xl font-semibold text-gray-900 m-0">Services</h3>
                                </div>

                                {["license", "non-license"].map((type) => (
                                    <section key={type} className="space-y-3" >
                                        {questions
                                            .filter(q => (type === "license" ? q.is_license : !q.is_license))
                                            .map(question => (
                                                <div key={question.id} className='flex items-center justify-between p-4 rounded-lg border border-gray-500 bg-white  hover:bg-white/80 transition-all duration-200'>
                                                    <div className='flex items-center space-x-3 w-[80%]'>
                                                        <div className='text-2xl font-medium text-black cursor-pointer'>{question.question}</div>
                                                    </div>
                                                    <div
                                                        onClick={() => toggleQuestion(question.id)}
                                                        className={`relative inline-flex h-8 w-16 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent  transition-colors duration-200 ease-in-out shadow focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 ${formData.services.includes(question.id) ? 'bg-emerald-600' : 'bg-gray-200'
                                                            }`}
                                                    >
                                                        <span
                                                            className={`pointer-events-none inline-block h-7 w-7 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${formData.services.includes(question.id) ? 'translate-x-8' : 'translate-x-0'
                                                                }`}
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                    </section>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SignUp;